import { useEffect, useRef, useState } from 'react';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { useAtom, useSetAtom } from 'jotai';

import type { LatestImage } from '@/@types/latestImages';
import type { ValidUniversalToothId } from '@videa/utils/src/normalizeToothIds';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { showPastVisitsAtom } from '../atoms';
import { compareImagesAtom } from '../multiFileViewer/imageToolbar/atoms';

import { CompareOdontogram } from './compareSection/CompareOdontogram';
import { CompareSection } from './compareSection/CompareSection';
import { ShareWithPatientDropdown } from './ShareWithPatientDropdown';

export const Compare = () => {
  const [selectedTeeth, setSelectedTeeth] = useState<ValidUniversalToothId[]>(
    [],
  );
  const [leftSectionImage, setLeftSectionImage] = useState<LatestImage | null>(
    null,
  );
  const [rightSectionImage, setRightSectionImage] =
    useState<LatestImage | null>(null);
  const [leftVisitId, setLeftVisitId] = useState<number | null>(null);
  const [rightVisitId, setRightVisitId] = useState<number | null>(null);

  const setCompareImages = useSetAtom(compareImagesAtom);
  const [showPastVisits, setShowPastVisits] = useAtom(showPastVisitsAtom);
  const { trackEvent } = useTrackEvent();
  const enterTimeRef = useRef(Date.now());

  // Update compareImagesAtom whenever image selections change
  useEffect(() => {
    if (leftSectionImage && rightSectionImage && leftVisitId && rightVisitId) {
      setCompareImages({
        leftImage: leftSectionImage,
        rightImage: rightSectionImage,
        leftVisitId,
        rightVisitId,
      });
    }
  }, [
    leftSectionImage,
    rightSectionImage,
    leftVisitId,
    rightVisitId,
    setCompareImages,
  ]);

  useEffect(() => {
    enterTimeRef.current = Date.now();

    // Cleanup function to track exit and duration
    return () => {
      if (enterTimeRef.current) {
        const exitTime = Date.now();
        const duration = exitTime - enterTimeRef.current;

        trackEvent(mixpanelEvents.compareViewDuration, {
          duration,
          durationSeconds: Math.round(duration / 1000),
        });
      }
    };
    // Track Event as a dependency causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToReview = () => {
    setShowPastVisits(!showPastVisits);

    if (enterTimeRef.current) {
      const exitTime = Date.now();
      const duration = exitTime - enterTimeRef.current;

      trackEvent(mixpanelEvents.compareViewExit, {
        timestamp: exitTime,
        duration,
        exitMethod: 'back_button',
      });
    }
  };

  const handleToothSelect = (toothId: ValidUniversalToothId) => {
    setSelectedTeeth((prev) => {
      // If the same tooth is clicked, deselect it
      if (prev.includes(toothId)) {
        return [];
      } else {
        // Otherwise, select only this tooth (single selection)
        return [toothId];
      }
    });

    trackEvent(mixpanelEvents.compareToothSelect, {
      toothId,
      isSelected: !selectedTeeth.includes(toothId),
    });
  };

  return (
    <div
      className="vai-h-full vai-p-0 vai-my-2 vai-ml-auto vai-w-[calc(100vw-80px)] vai-flex vai-flex-col"
      
    >
      <div className="vai-flex vai-items-center vai-justify-between vai-px-4 vai-py-2 vai-flex-shrink-0">
        <div className="vai-flex vai-items-center vai-gap-2">
          <Button
            data-testid="back-to-review-mode-button"
            variant="ghost"
            className="vai-w-fit vai-flex vai-items-center vai-gap-2 vai-text-zinc-50 hover:vai-text-zinc-50 vai-px-3 vai-py-2 hover:vai-bg-zinc-700"
            onClick={handleBackToReview}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="vai-w-5 vai-h-5" />
            <span>
              <Trans>Back to Review Mode</Trans>
            </span>
          </Button>

          {rightSectionImage && leftSectionImage && (
            <ShareWithPatientDropdown
              hasActiveImage={true}
              source="compare_modal"
            />
          )}
        </div>

        <CompareOdontogram
          onToothSelect={handleToothSelect}
          selectedTeeth={selectedTeeth}
        />
      </div>

      <div className="vai-w-full vai-flex-1 vai-flex vai-justify-center vai-items-stretch vai-gap-[78px] vai-p-2 vai-min-h-0">
        <div className="vai-flex-1">
          <CompareSection
            section="left"
            rightSectionImage={rightSectionImage}
            onImageChange={(image) => setLeftSectionImage(image)}
            selectedTeeth={selectedTeeth}
            onVisitChange={(visitId) => setLeftVisitId(visitId)}
          />
        </div>
        <div className="vai-flex-1">
          <CompareSection
            section="right"
            onImageChange={(image) => setRightSectionImage(image)}
            selectedTeeth={selectedTeeth}
            onVisitChange={(visitId) => setRightVisitId(visitId)}
          />
        </div>
      </div>
    </div>
  );
};
